import React, { useState, useEffect } from 'react';
import { Shield, Award, Clock, Users, AlertCircle } from 'lucide-react';
import { PricingSection } from '../components/PricingSection';
import { TestimonialsSlider } from '../components/TestimonialsSlider';
import { ProcessSlider } from '../components/ProcessSlider';
import { MobileCarousel } from '../components/MobileCarousel';
import { AttorneysSection } from '../components/AttorneysSection';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { useLeadForm } from '../hooks/useLeadForm';
import { getDialCodeFromCountry, getCountryCodesForSelect } from '../utils/countryMapping';
import { openZendeskChat } from '../utils/zendesk';

export const Home = ({ onOpenPopup }) => {
  const { geolocationData, isLoading, error, submitForm, clearError } = useLeadForm();
  const [heroFormData, setHeroFormData] = useState({
    name: '', email: '', countryCode: '+1', country: 'US', contactNumber: '', message: ''
  });

  const countryCodesOptions = getCountryCodesForSelect();

  useEffect(() => {
    if (geolocationData?.data?.countryCode) {
      const cc = geolocationData.data.countryCode;
      const dc = getDialCodeFromCountry(cc);
      setHeroFormData(prev => ({ ...prev, country: cc, countryCode: `+${dc}` }));
    }
  }, [geolocationData]);

  const handleHeroFormSubmit = async (e) => {
    e.preventDefault();
    clearError();
    await submitForm(heroFormData);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#58225a] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhhbmRzaGFrZSUyMGRlYWx8ZW58MHx8fHwxNzc2Mjg0ODk2fDA&ixlib=rb-4.1.0&q=85"
            alt="Two professionals shaking hands across a table"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#58225a]/90 via-[#58225a]/80 to-[#58225a]/60"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Build Strong <span className="text-[#e8a8c8]">Legal Protection</span> for Your Brand
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-gray-300">
                Trademark Examining Lawyer connects businesses with knowledgeable trademark professionals who handle the USPTO registration process while you focus on expanding your company.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" onClick={onOpenPopup} data-testid="hero-register-btn"
                  className="bg-[#ba2a84] text-white hover:bg-[#8a1f63] text-lg px-8 py-6 font-semibold">
                  File My Trademark
                </Button>
                <Button size="lg" variant="outline" onClick={openZendeskChat} data-testid="hero-chat-btn"
                  className="js-chat  border-white text-white hover:bg-white hover:text-[#58225a] text-lg px-8 py-6 font-semibold">
                  Start a Chat
                </Button>
              </div>
            </div>

            <div>
              <Card className="p-8 shadow-2xl bg-white" data-testid="hero-lead-form">
                <h2 className="text-2xl font-bold text-[#58225a] mb-2 whitespace-nowrap">Launch Your Application Today</h2>
                <p className="text-gray-600 mb-6">Submit your information and one of our representatives will contact you within one business day.</p>
                
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                <form onSubmit={handleHeroFormSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="hero-name" className="text-gray-700">Name *</Label>
                    <Input id="hero-name" type="text" required value={heroFormData.name} onChange={(e) => setHeroFormData({ ...heroFormData, name: e.target.value })} className="mt-1" placeholder="Your full name" disabled={isLoading} />
                  </div>
                  <div>
                    <Label htmlFor="hero-email" className="text-gray-700">Email *</Label>
                    <Input id="hero-email" type="email" required value={heroFormData.email} onChange={(e) => setHeroFormData({ ...heroFormData, email: e.target.value })} className="mt-1" placeholder="Your email address" disabled={isLoading} />
                  </div>
                  {geolocationData?.data?.countryName && (
                    <div>
                      <Label className="text-gray-700">Country</Label>
                      <div className="mt-1 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <p className="text-gray-900 font-medium">{geolocationData.data.countryName}</p>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label htmlFor="hero-contact" className="text-gray-700">Contact Number *</Label>
                    <div className="flex gap-2 mt-1">
                      <Select value={heroFormData.countryCode} onValueChange={(value) => { const sc = countryCodesOptions.find(c => c.code === value); setHeroFormData({ ...heroFormData, countryCode: value, country: sc?.countryCode || 'US' }); }} disabled={isLoading}>
                        <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>{countryCodesOptions.map((item) => (<SelectItem key={item.code} value={item.code}>{item.code}</SelectItem>))}</SelectContent>
                      </Select>
                      <Input id="hero-contact" type="tel" required value={heroFormData.contactNumber} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); if (v.length <= 15) setHeroFormData({ ...heroFormData, contactNumber: v }); }} placeholder="Phone number" className="flex-1" maxLength={15} disabled={isLoading} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="hero-message" className="text-gray-700">Message</Label>
                    <Textarea id="hero-message" value={heroFormData.message} onChange={(e) => setHeroFormData({ ...heroFormData, message: e.target.value })} className="mt-1" placeholder="Describe your trademark needs..." rows={4} disabled={isLoading} />
                  </div>
                  <Button type="submit" data-testid="hero-form-submit-btn" className="w-full bg-[#ba2a84] hover:bg-[#8a1f63] text-white py-6 text-lg font-semibold" disabled={isLoading}>
                    {isLoading ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch max-w-6xl mx-auto">
            <div className="order-2 lg:order-1 w-full">
              <img
                src="https://images.unsplash.com/photo-1642522029686-5485ea7e6042?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxsYXd5ZXIlMjByZXZpZXdpbmclMjBkb2N1bWVudHMlMjBkZXNrJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3NjI4NzUxNXww&ixlib=rb-4.1.0&q=85"
                alt="Professional reviewing legal documents at desk"
                className="w-full h-full object-cover rounded-2xl shadow-2xl"
              />
            </div>
            <div className="order-1 lg:order-2 w-full">
              <h2 className="text-4xl font-bold text-[#58225a] mb-6">
                Protecting Business Identities Is Our Priority
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                At Trademark Examining Lawyer, we are committed to helping business owners and organizations access dependable intellectual property solutions without the complexity or excessive fees often associated with conventional legal providers.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our experienced trademark professionals have supported thousands of clients with federal trademark applications, office action responses, and ongoing registration maintenance. With dependable service and straightforward pricing, you'll know exactly what to expect throughout the process.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { t: "Qualified Trademark Professionals", d: "Every submission undergoes review by a licensed attorney before filing." },
                  { t: "Complete Case Coordination", d: "Your assigned representative manages every stage from search review through registration completion." },
                  { t: "Clear, Upfront Pricing", d: "No surprise fees. Every package details exactly what is included." }
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary flex items-center justify-center mt-1">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#58225a] mb-1">{item.t}</h3>
                      <p className="text-gray-600">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" onClick={onOpenPopup} className="bg-[#ba2a84] hover:bg-[#8a1f63] text-white px-8 py-6 text-lg font-semibold">
                  File My Trademark
                </Button>
                <Button size="lg" variant="outline" onClick={openZendeskChat} className="js-chat border-2 border-[#58225a] text-[#58225a] hover:bg-gray-50 px-8 py-6 text-lg font-semibold">
                  Start a Chat
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="hidden md:grid md:grid-cols-4 gap-8">
            {[
              { v: "100+", l: "Industries Served" },
              { v: "98%", l: "Success Percentage" },
              { v: "30+", l: "Professionals" },
              { v: "24/7", l: "Customer Assistance" }
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-primary mb-2">{s.v}</div>
                <div className="text-gray-600">{s.l}</div>
              </div>
            ))}
          </div>
          <MobileCarousel autoPlayInterval={3000}>
            {[
              { v: "100+", l: "Industries Served" },
              { v: "98%", l: "Success Percentage" },
              { v: "30+", l: "Professionals" },
              { v: "24/7", l: "Customer Assistance" }
            ].map((s, i) => (
              <div key={i} className="text-center py-4">
                <div className="text-4xl font-bold text-[#e8a8c8] mb-2">{s.v}</div>
                <div className="text-gray-600">{s.l}</div>
              </div>
            ))}
          </MobileCarousel>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#58225a] mb-4">Why Businesses Trust Trademark Examining Lawyer</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We combine legal knowledge with dedicated customer support to provide meaningful results.
            </p>
          </div>
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: Shield, t: "Comprehensive Trademark Coverage", d: "From preliminary searches to ongoing trademark supervision, we safeguard every aspect of your brand." },
              { icon: Award, t: "Experienced Legal Professionals", d: "Our attorneys contribute extensive intellectual property expertise to every matter." },
              { icon: Clock, t: "Efficient Processing Times", d: "Optimized procedures and priority options help reduce delays and accelerate filings." },
              { icon: Users, t: "Dedicated Support Representative", d: "One reliable contact who understands every detail of your trademark matter." }
            ].map((c, i) => (
              <Card key={i} className="p-6 text-center hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-white mb-4">
                  <c.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-[#58225a] mb-3">{c.t}</h3>
                <p className="text-gray-600">{c.d}</p>
              </Card>
            ))}
          </div>
          <MobileCarousel>
            {[
              { icon: Shield, t: "Comprehensive Trademark Coverage", d: "From preliminary searches to ongoing trademark supervision, we safeguard every aspect of your brand." },
              { icon: Award, t: "Experienced Legal Professionals", d: "Our attorneys contribute extensive intellectual property expertise to every matter." },
              { icon: Clock, t: "Efficient Processing Times", d: "Optimized procedures and priority options help reduce delays and accelerate filings." },
              { icon: Users, t: "Dedicated Support Representative", d: "One reliable contact who understands every detail of your trademark matter." }
            ].map((c, i) => (
              <Card key={i} className="p-6 text-center shadow-lg">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-white mb-4">
                  <c.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-[#58225a] mb-3">{c.t}</h3>
                <p className="text-gray-600">{c.d}</p>
              </Card>
            ))}
          </MobileCarousel>
        </div>
      </section>

      <ProcessSlider />
      <AttorneysSection />
      <PricingSection onOpenPopup={onOpenPopup} />
      <TestimonialsSlider />

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
            <div className="order-2 lg:order-1 w-full">
              <h2 className="text-4xl font-bold text-[#58225a] mb-6">Frequently Asked Questions</h2>
              <p className="text-lg text-gray-600 mb-8">
                Everything you should know before beginning your trademark registration process.
              </p>
              <Accordion type="single" collapsible className="space-y-4">
                {[
                  { q: "How long does trademark registration usually require?", a: "Most trademark applications take several months to over a year to complete. The exact timeline depends on USPTO review times and whether any objections arise." },
                  { q: "Which types of trademarks qualify for registration?", a: "Business names, logos, slogans, product names, and brand identifiers may qualify for registration. The mark must be distinctive and not likely to confuse consumers." },
                  { q: "Do I need an attorney to submit a trademark application?", a: "No, an attorney is not required to file a trademark application. However, legal guidance can help avoid mistakes and improve the likelihood of approval." },
                  { q: "What expenses are associated with trademark registration?", a: "Costs typically include government filing fees and any professional service fees. The total amount varies based on the number of classes and filing requirements." },
                  { q: "What is included in a complete trademark search?", a: "A comprehensive search reviews federal records, pending applications, business names, and common law uses. This helps identify potential conflicts before filing." },
                  { q: "Can a trademark similar to an existing one still be registered?", a: "Possibly, depending on how similar the marks are and whether consumer confusion is likely. A detailed review can help determine the chances of approval." }
                ].map((faq, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="bg-white border border-gray-200 rounded-lg px-6">
                    <AccordionTrigger className="text-left font-semibold text-[#58225a] hover:text-[#e8a8c8]">
                      <div className="flex items-center space-x-3">
                        <span className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">Q</span>
                        <span>{faq.q}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pl-11 pt-2">{faq.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              <div className="mt-8">
                <Button onClick={onOpenPopup} className="bg-[#ba2a84] hover:bg-[#8a1f63] text-white px-8 py-6 text-lg font-semibold">
                  File Your Trademark
                </Button>
              </div>
            </div>

            <div className="order-1 lg:order-2 w-full">
              <img
                src="https://images.unsplash.com/photo-1758518729706-b1810dd39cc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGF0dG9ybmV5JTIwY29uc3VsdGF0aW9uJTIwbWVldGluZ3xlbnwwfHx8fDE3NzYyODQ4ODB8MA&ixlib=rb-4.1.0&q=85"
                alt="Business professionals in a meeting"
                className="w-full h-full object-cover rounded-2xl shadow-2xl min-h-[600px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1772541800741-0ff177b2c282?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNpdHlzY2FwZSUyMHNreWxpbmUlMjBuaWdodCUyMGRhcmt8ZW58MHx8fHwxNzc2Mjg2OTQzfDA&ixlib=rb-4.1.0&q=85" alt="City skyline at dusk" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#58225a]/80"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">Start Protecting Your Brand Today</h2>
          <p className="text-xl mb-8 text-gray-300 max-w-2xl mx-auto">
            Join the many businesses that trust Trademark Examining Lawyer to safeguard their intellectual property. Begin your trademark application today.
          </p>
          <Button size="lg" onClick={onOpenPopup} className="bg-[#ba2a84] hover:bg-[#8a1f63] text-white text-lg px-8 py-6 font-semibold">
            File Your Trademark
          </Button>
        </div>
      </section>
    </div>
  );
};
